import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import useAnalytics from '../analytics/useAnalytics';
import { EVT_LIBRARY_VIEW, EVT_BOOKING_CREATE } from '../analytics/events';
import { auroraTopbarSx } from '../theme/auroraSurface';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  Skeleton,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Collapse,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Snackbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { keyframes } from '@mui/system';
import {
  LocationOn,
  EventSeat,
  ArrowBack,
  CheckCircle,
  CalendarToday,
  Info,
  Check,
  Schedule,
  ExpandMore,
  FavoriteBorder,
  Share,
  AccessTime,
  AccountBalanceWallet,
  Storefront,
  LocalOffer,
  WhatsApp,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { AuthContext } from '../context/AuthContext';
import { AppBarContext, useAppBarConfig } from '../context/AppBarContext';
import api from '../utils/api';
import StudentBottomNav from '../components/StudentBottomNav';
import DesktopNav from '../components/DesktopNav';
import BottomSheet from '../components/shared/BottomSheet';
import GradientSurface from '../components/shared/GradientSurface';
import LibraryRouteMap from '../components/LibraryRouteMap';
import { PALETTE_LIGHT } from '../theme/tokens';

const chipFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
`;

// Load Razorpay script
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const MotionCard = motion(Card);

function LibraryDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, loading: authLoading } = useContext(AuthContext);
  const { track } = useAnalytics();
  const viewTrackedRef = useRef(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Detect whether we are mounted inside the StudentPortal shell
  const appBarCtx = useContext(AppBarContext);
  const inStudentShell = appBarCtx != null;

  const pendingPlanId = location.state?.pendingPlanId;
  const pendingPaymentMethod = location.state?.pendingPaymentMethod;
  const pendingStartDate = location.state?.pendingStartDate;

  // Support deep-link via ?plan=<id> (sent by library mini-websites).
  // location.state takes precedence so in-app navigations are unaffected.
  const planFromQuery = new URLSearchParams(location.search).get('plan');
  const effectivePendingPlanId = pendingPlanId != null ? pendingPlanId : planFromQuery;

  const [library, setLibrary] = useState(null);
  const [plans, setPlans] = useState([]);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
  const [backendAuthStatus, setBackendAuthStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [error, setError] = useState('');
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingSuccessMessage, setBookingSuccessMessage] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [amenitiesExpanded, setAmenitiesExpanded] = useState(false);
  const [walletBalance, setWalletBalance] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('token');
  const [tokenBookingLoading, setTokenBookingLoading] = useState(false);
  const [rechargeAndBookLoading, setRechargeAndBookLoading] = useState(false);
  const [shareSnackbar, setShareSnackbar] = useState(false);

  const handleShare = async () => {
    if (!library) return;
    const shareUrl = `${window.location.origin}/libraries/${id}`;
    const shareText = `Check out ${library.name} on BookMyLibrary!`;

    if (navigator.share) {
      try {
        await navigator.share({ title: library.name, text: shareText, url: shareUrl });
      } catch (err) {
        if (err.name !== 'AbortError') {
          await navigator.clipboard.writeText(shareUrl);
          setShareSnackbar(true);
        }
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      setShareSnackbar(true);
    }
  };

  // Always call this hook — it is a no-op when ctx is null (public flow)
  useAppBarConfig(
    {
      title: 'Library details',
      backTo: () => navigate(-1),
      action: (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" aria-label="Add to favourites">
            <FavoriteBorder />
          </IconButton>
          <IconButton size="small" onClick={handleShare} aria-label="Share">
            <Share />
          </IconButton>
        </Box>
      ),
    },
    []
  );

  useEffect(() => {
    if (!authLoading) {
      fetchLibraryDetails();
    }
  }, [id, isAuthenticated, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Open the booking prompt for a deep-linked plan. Works for guests too:
    // the dialog shows the register CTA, mirroring the in-page "Book your Seat"
    // button (handleBookPlan) which is not auth-gated either.
    if (effectivePendingPlanId != null && plans.length > 0) {
      // Coerce both sides to String so integer plan.id matches the URL string param.
      const pendingPlan = plans.find((p) => String(p.id) === String(effectivePendingPlanId));
      if (pendingPlan) {
        setSelectedPlan(pendingPlan);
        setStartDate(pendingStartDate || '');
        setError('');
        setPaymentMethod(pendingPaymentMethod || 'token');
        setBookingDialogOpen(true);
        // Clean both location.state AND the ?plan= query string so a refresh
        // doesn't re-trigger the dialog.
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [isAuthenticated, effectivePendingPlanId, plans]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLibraryDetails = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem('accessToken');
      console.log('[LibraryDetail] Fetching with:', {
        isAuthenticated,
        authLoading,
        hasToken: !!token,
        tokenPrefix: token ? token.substring(0, 20) + '...' : 'none',
      });

      const [libraryRes, plansRes] = await Promise.all([
        api.get(`/libraries/${id}`),
        api.get(`/libraries/${id}/plans`),
      ]);
      setLibrary(libraryRes.data.data.library);
      if (!viewTrackedRef.current) {
        viewTrackedRef.current = true;
        track(EVT_LIBRARY_VIEW, { libraryId: id });
      }
      setPlans(plansRes.data.data.plans);
      setIsFirstTimeUser(plansRes.data.data.is_first_time_user || false);
      setBackendAuthStatus(plansRes.data.data.auth_status || null);

      const responseFirstTimeUser = plansRes.data.data.is_first_time_user;
      const authStatus = plansRes.data.data.auth_status;
      const plansWithDiscounts = plansRes.data.data.plans.filter((p) => p.first_time_discount);

      console.log('[LibraryDetail] Response:', {
        is_first_time_user: responseFirstTimeUser,
        auth_status: authStatus,
        plansCount: plansRes.data.data.plans.length,
        plansWithDiscount: plansWithDiscounts.length,
        firstPlan: plansRes.data.data.plans[0]
          ? {
              id: plansRes.data.data.plans[0].id,
              name: plansRes.data.data.plans[0].name,
              price: plansRes.data.data.plans[0].price_cents,
              first_time_discount: plansRes.data.data.plans[0].first_time_discount,
              discounted_price: plansRes.data.data.plans[0].discounted_price,
            }
          : 'no plans',
      });

      if (token && authStatus && authStatus !== 'authenticated' && authStatus !== 'no_token') {
        console.warn(`[LibraryDetail] Token validation failed on backend: ${authStatus}`);
        console.warn('[LibraryDetail] Token may be expired or invalid. Consider refreshing.');
        if (authStatus === 'token_expired' || authStatus === 'token_invalid') {
          console.warn('[LibraryDetail] Clearing invalid token from localStorage');
        }
      }

      if (isAuthenticated && !responseFirstTimeUser && plansWithDiscounts.length > 0 && authStatus === 'authenticated') {
        console.warn('[LibraryDetail] User is authenticated but not first-time. User likely already has a booking at this library.');
      }
      if (isAuthenticated && authStatus !== 'authenticated') {
        console.warn(`[LibraryDetail] Frontend thinks user is authenticated but backend says: ${authStatus}`);
      }
      if (!isAuthenticated && token) {
        console.warn('[LibraryDetail] WARNING: Token exists in localStorage but isAuthenticated is false. Auth context may not have loaded properly.');
      }
    } catch (err) {
      console.error('[LibraryDetail] Fetch error:', err);
      setError('Failed to load library details');
    } finally {
      setLoading(false);
    }
  };

  const fetchWalletBalance = async () => {
    try {
      setWalletLoading(true);
      const res = await api.get('/wallet/balance');
      setWalletBalance(res.data.data.wallet?.balance || 0);
    } catch (err) {
      console.error('Error fetching wallet:', err);
      setWalletBalance(0);
    } finally {
      setWalletLoading(false);
    }
  };

  const handleBookPlan = (plan) => {
    setSelectedPlan(plan);
    setStartDate('');
    setError('');
    setPaymentMethod('token');
    setBookingDialogOpen(true);
  };

  // Guest flow: after choosing a plan + payment method, send the user to create
  // an account, carrying the selection so the booking resumes post-signup.
  const proceedToRegister = () => {
    if (!startDate) {
      setError('Please select a start date');
      return;
    }
    navigate('/register', {
      state: {
        from: location.pathname,
        pendingPlanId: selectedPlan?.id,
        pendingPaymentMethod: paymentMethod,
        pendingStartDate: startDate,
      },
    });
  };

  const getEffectivePrice = (plan) => {
    if (plan.first_time_discount && plan.discounted_price) {
      return plan.discounted_price;
    }
    return plan.price_cents;
  };

  // Compute token (BML booking fee) and remainder from commission_rate_effective
  const getCommissionSplit = (plan) => {
    const effectivePrice = getEffectivePrice(plan);
    const rate = library?.commission_rate_effective;
    if (!rate || rate <= 0) {
      return { token: 0, remainder: effectivePrice };
    }
    const token = Math.round(effectivePrice * rate / 100);
    const remainder = effectivePrice - token;
    return { token, remainder };
  };

  const confirmBooking = async () => {
    if (!startDate) {
      setError('Please select a start date');
      return;
    }

    if (paymentMethod === 'token') {
      // Open Razorpay for the commission amount
      try {
        setTokenBookingLoading(true);
        setError('');

        const bookingRes = await api.post('/bookings', {
          library_id: id,
          plan_id: selectedPlan.id,
          start_date: startDate,
          payment_method: 'token',
        });

        const { booking, commission_order } = bookingRes.data.data || bookingRes.data;
        const amountPaise = (selectedPlan.price_cents || 0) * 100;
        track(EVT_BOOKING_CREATE, { libraryId: id, planId: selectedPlan.id, amountPaise }, amountPaise / 100);

        if (!commission_order) {
          // No commission due — navigate directly
          setBookingSuccessMessage(bookingRes.data?.message || '');
          setBookingDialogOpen(false);
          setBookingSuccess(true);
          setTimeout(() => {
            navigate('/student/access', { state: { tab: 'bookings' } });
          }, 3000);
          setTokenBookingLoading(false);
          return;
        }

        const scriptLoaded = await loadRazorpayScript();
        if (!scriptLoaded) {
          setError('Failed to load payment gateway. Please try again.');
          setTokenBookingLoading(false);
          return;
        }

        const { order_id, amount: orderAmount, currency, key_id, prefill } = commission_order;

        const options = {
          key: key_id,
          amount: orderAmount,
          currency: currency || 'INR',
          name: 'BookMyLibrary',
          description: `BML Booking Fee — ${library.name}`,
          order_id: order_id,
          prefill: {
            name: prefill?.name || user?.name || '',
            email: prefill?.email || user?.email || '',
            contact: prefill?.contact || '',
          },
          theme: { color: PALETTE_LIGHT.primary.main },
          handler: async function (response) {
            try {
              await api.post(`/bookings/${booking.id}/verify-token`, {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              setBookingDialogOpen(false);
              setBookingSuccess(true);
              setBookingSuccessMessage('Booking confirmed');
              setTimeout(() => {
                navigate('/student/access', { state: { tab: 'bookings' } });
              }, 3000);
            } catch (verifyErr) {
              console.error('Token verification failed:', verifyErr);
              setError('Payment received but verification failed. Please contact support with your payment ID.');
            }
            setTokenBookingLoading(false);
          },
          modal: {
            ondismiss: function () {
              setTokenBookingLoading(false);
              // Booking exists as pending — inform the user gracefully
              setError('Payment window closed. Your booking is pending — open it again to complete the BML booking fee payment.');
            },
          },
        };

        const razorpay = new window.Razorpay(options);
        razorpay.on('payment.failed', function (response) {
          setTokenBookingLoading(false);
          setError(response.error.description || 'Payment failed. Please try again.');
        });
        razorpay.open();
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to create booking');
        setTokenBookingLoading(false);
      }
      return;
    }

    // pay_at_library path
    try {
      setBookingLoading(true);
      setError('');
      const bookingRes = await api.post('/bookings', {
        library_id: id,
        plan_id: selectedPlan.id,
        start_date: startDate,
        payment_method: 'pay_at_library',
      });
      const amountPaise = (selectedPlan.price_cents || 0) * 100;
      track(EVT_BOOKING_CREATE, { libraryId: id, planId: selectedPlan.id, amountPaise }, amountPaise / 100);
      setBookingSuccessMessage(bookingRes.data?.message || '');
      setBookingDialogOpen(false);
      setBookingSuccess(true);
      setTimeout(() => {
        navigate('/student/access', { state: { tab: 'bookings' } });
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create booking');
    } finally {
      setBookingLoading(false);
    }
  };

  // Kept as a dead stub — wallet recharge path removed from booking flow.
  const handleRechargeAndBook = () => {};

  const getGalleryImages = () => {
    if (library.gallery && library.gallery.length > 0) {
      return library.gallery.map(
        (img) =>
          `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${img}`
      );
    }
    return [
      'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=1200&h=400&fit=crop',
      'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=1200&h=400&fit=crop',
      'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=1200&h=400&fit=crop',
    ];
  };

  const handleNextImage = () => {
    const images = getGalleryImages();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrevImage = () => {
    const images = getGalleryImages();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh' }}>
        <Container maxWidth="sm" sx={{ py: 2 }}>
          <Skeleton variant="rectangular" height={250} sx={{ borderRadius: 3, mb: 2 }} />
          <Skeleton variant="text" height={40} width="60%" />
          <Skeleton variant="text" height={30} width="80%" />
          <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 3, mt: 2 }} />
        </Container>
      </Box>
    );
  }

  if (!library) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="h6">Library not found</Typography>
      </Box>
    );
  }

  const hasAuthIssue =
    isAuthenticated && backendAuthStatus && backendAuthStatus !== 'authenticated';

  const hasCoords = Number.isFinite(Number(library.lat)) && Number.isFinite(Number(library.lng));
  const mapsQuery = hasCoords
    ? `${library.lat},${library.lng}`
    : encodeURIComponent(`${library.name} ${library.address || ''}`.trim());
  const isAppleDevice = typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent);
  const mapsUrl = isAppleDevice
    ? `https://maps.apple.com/?q=${mapsQuery}${hasCoords ? `&ll=${library.lat},${library.lng}` : ''}`
    : `https://www.google.com/maps/search/?api=1&query=${mapsQuery}`;

  // Compose a readable address from whatever fields are present (deduped).
  const fullAddress = [
    library.address || library.address_line1,
    library.address_line2,
    library.locality,
    library.city,
    library.state,
    library.pincode,
  ]
    .map((part) => (part == null ? '' : String(part).trim()))
    .filter(Boolean)
    .filter((part, idx, arr) => arr.indexOf(part) === idx)
    .join(', ');

  const BookingContent = () => (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {selectedPlan && (
        <>
          <Box mb={2}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              LIBRARY
            </Typography>
            <Typography variant="subtitle1" fontWeight={600}>
              {library.name}
            </Typography>
          </Box>
          <Box mb={2}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              PLAN
            </Typography>
            <Typography variant="subtitle1" fontWeight={600}>
              {selectedPlan.name} ({selectedPlan.duration_days} days)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedPlan.hours_per_day
                ? `${selectedPlan.hours_per_day} hours/day limit`
                : 'Unlimited hours'}
            </Typography>
          </Box>
          <Box mb={2}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              AMOUNT TO PAY
            </Typography>
            {selectedPlan.first_time_discount && selectedPlan.discounted_price ? (
              <Box>
                <Box display="flex" alignItems="baseline" gap={1}>
                  <Typography variant="h5" fontWeight={800} color="primary.main">
                    ₹{selectedPlan.discounted_price}
                  </Typography>
                  <Typography variant="body1" fontWeight={500} color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                    ₹{selectedPlan.price_cents}
                  </Typography>
                </Box>
                <Chip
                  icon={<LocalOffer sx={{ fontSize: 14 }} />}
                  label={`${selectedPlan.first_time_discount}% off for first-time users`}
                  size="small"
                  color="success"
                  sx={{ mt: 0.5, fontWeight: 600 }}
                />
              </Box>
            ) : (
              <Typography variant="h5" fontWeight={800} color="primary.main">
                ₹{selectedPlan.price_cents}
              </Typography>
            )}
          </Box>
          <TextField
            fullWidth
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: new Date().toISOString().split('T')[0] }}
            sx={{ mb: 2 }}
          />

          {/* Payment method */}
          {selectedPlan && (() => {
            const { token, remainder } = getCommissionSplit(selectedPlan);
            const effectivePrice = getEffectivePrice(selectedPlan);
            return (
              <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
                <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
                  Payment Method
                </FormLabel>
                <RadioGroup value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                  <Box
                    sx={{
                      border: 1,
                      borderColor: paymentMethod === 'token' ? 'primary.main' : 'divider',
                      borderRadius: 2,
                      p: 1.5,
                      mb: 1,
                      bgcolor: paymentMethod === 'token' ? 'primary.soft' : 'background.paper',
                      cursor: 'pointer',
                    }}
                    onClick={() => setPaymentMethod('token')}
                  >
                    <FormControlLabel
                      value="token"
                      control={<Radio />}
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <AccountBalanceWallet fontSize="small" color="primary" />
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              Pay ₹{token} now (Confirmed Seat Booking)
                            </Typography>
                            <Typography variant="caption" color="success.main" display="block">
                              Refundable upon cancellation.
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Pay ₹{remainder} at the library
                            </Typography>
                          </Box>
                        </Box>
                      }
                      sx={{ margin: 0 }}
                    />
                  </Box>
                  <Box
                    sx={{
                      border: 1,
                      borderColor: paymentMethod === 'pay_at_library' ? 'success.main' : 'divider',
                      borderRadius: 2,
                      p: 1.5,
                      bgcolor: paymentMethod === 'pay_at_library' ? 'success.50' : 'background.paper',
                      cursor: 'pointer',
                    }}
                    onClick={() => setPaymentMethod('pay_at_library')}
                  >
                    <FormControlLabel
                      value="pay_at_library"
                      control={<Radio color="success" />}
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Storefront fontSize="small" color="success" />
                          <Box>
                            <Typography variant="body2" fontWeight={600}>Pay at Library</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Full amount ₹{effectivePrice} due at the counter
                            </Typography>
                          </Box>
                        </Box>
                      }
                      sx={{ margin: 0 }}
                    />
                  </Box>
                </RadioGroup>
              </FormControl>
            );
          })()}

          {paymentMethod === 'token' && selectedPlan && (
            <Alert severity="info" icon={<Info />} sx={{ borderRadius: 2, mb: 2 }}>
              You'll pay the small BML booking fee online now. The remaining ₹{getCommissionSplit(selectedPlan).remainder} is paid directly at the library.
            </Alert>
          )}

          {paymentMethod === 'pay_at_library' && selectedPlan && (
            <Alert severity="warning" icon={<Info />} sx={{ borderRadius: 2, mb: 2 }}>
              Show your booking confirmation at the library counter and pay ₹{getEffectivePrice(selectedPlan)} before check-in.
            </Alert>
          )}

          {!isAuthenticated && (
            <Alert severity="info" icon={<Info />} sx={{ borderRadius: 2, mt: 2 }}>
              Next, create your account — then you'll {paymentMethod === 'pay_at_library' ? 'confirm your booking' : 'complete the BML booking fee payment'}.
            </Alert>
          )}

          {/* Action buttons rendered inside the sheet/dialog */}
          <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
            <Button
              onClick={() => setBookingDialogOpen(false)}
              variant="outlined"
              sx={{ flex: 1, borderRadius: 2 }}
            >
              Cancel
            </Button>
            {!isAuthenticated ? (
              <Button
                onClick={proceedToRegister}
                variant="contained"
                color={paymentMethod === 'pay_at_library' ? 'success' : 'primary'}
                sx={{ flex: 2, borderRadius: 2 }}
              >
                Continue
              </Button>
            ) : (
              <Button
                onClick={confirmBooking}
                variant="contained"
                disabled={bookingLoading || tokenBookingLoading}
                color={paymentMethod === 'pay_at_library' ? 'success' : 'primary'}
                sx={{ flex: 2, borderRadius: 2 }}
              >
                {bookingLoading || tokenBookingLoading
                  ? 'Processing...'
                  : paymentMethod === 'pay_at_library'
                  ? 'Book Now (Pay at Library)'
                  : selectedPlan
                  ? `Pay ₹${getCommissionSplit(selectedPlan).token} Booking Fee`
                  : 'Book Now'}
              </Button>
            )}
          </Box>
        </>
      )}
    </>
  );

  return (
    <Box sx={{ minHeight: '100vh', pb: { xs: 10, md: 0 } }}>
      {!inStudentShell && <DesktopNav />}

      {hasAuthIssue && (
        <Alert severity="warning" sx={{ m: 2 }}>
          Session issue detected ({backendAuthStatus}). Please try logging out and back in.
        </Alert>
      )}

      {/* Sticky header (mobile) — public flow only */}
      {!inStudentShell && (
      <Box
        sx={(theme) => ({
          ...auroraTopbarSx(theme),
          display: { xs: 'block', md: 'none' },
          position: 'sticky',
          top: 0,
          zIndex: 100,
          borderBottom: 1,
          borderColor: 'divider',
          boxShadow: 1,
        })}
      >
        <Container maxWidth="md">
          <Box display="flex" alignItems="center" justifyContent="space-between" py={1.5}>
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton onClick={() => navigate(-1)} size="small" aria-label="Go back">
                <ArrowBack />
              </IconButton>
              <Typography variant="h6" fontWeight={700}>
                Library Details
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              <IconButton size="small" aria-label="Add to favourites">
                <FavoriteBorder />
              </IconButton>
              <IconButton size="small" onClick={handleShare} aria-label="Share">
                <Share />
              </IconButton>
            </Box>
          </Box>
        </Container>
      </Box>
      )}

      {/* Hero image carousel */}
      <Box sx={{ position: 'relative', height: 250, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            transition: 'transform 0.5s ease-in-out',
            transform: `translateX(-${currentImageIndex * 100}%)`,
            height: '100%',
          }}
        >
          {getGalleryImages().map((image, index) => (
            <Box
              key={index}
              component="img"
              src={image}
              alt={`${library.name} ${index + 1}`}
              onClick={() => openLightbox(index)}
              loading="lazy"
              decoding="async"
              sx={{ width: '100%', height: 250, objectFit: 'cover', flexShrink: 0, cursor: 'pointer' }}
            />
          ))}
        </Box>

        {/* Back button floating on hero */}
        <IconButton
          onClick={() => navigate(-1)}
          aria-label="Go back"
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            bgcolor: 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(8px)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
            display: { xs: 'none', md: 'flex' },
          }}
        >
          <ArrowBack />
        </IconButton>

        {getGalleryImages().length > 1 && (
          <>
            <IconButton
              onClick={handlePrevImage}
              aria-label="Previous image"
              sx={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.9)',
                backdropFilter: 'blur(10px)',
                '&:hover': { bgcolor: 'common.white' },
                boxShadow: 2,
              }}
            >
              <ArrowBack />
            </IconButton>
            <IconButton
              onClick={handleNextImage}
              aria-label="Next image"
              sx={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.9)',
                backdropFilter: 'blur(10px)',
                '&:hover': { bgcolor: 'common.white' },
                boxShadow: 2,
              }}
            >
              <ArrowBack sx={{ transform: 'rotate(180deg)' }} />
            </IconButton>

            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: 1,
              }}
            >
              {getGalleryImages().map((_, index) => (
                <Box
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  sx={{
                    width: currentImageIndex === index ? 24 : 8,
                    height: 8,
                    borderRadius: 4,
                    bgcolor:
                      currentImageIndex === index ? 'common.white' : 'rgba(255,255,255,0.5)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                  }}
                />
              ))}
            </Box>
          </>
        )}
      </Box>

      <Container maxWidth="md" sx={{ mt: -3 }}>
        {/* Library info card */}
        <MotionCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          sx={{ borderRadius: 4, boxShadow: 3, mb: 1.5 }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1.5} mb={amenitiesExpanded ? 2 : 0}>
              <Box flex={1} minWidth={0}>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  {library.name}
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Chip
                    label={library.active ? 'Open Now' : 'Closed'}
                    color={library.active ? 'success' : 'default'}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip
                    icon={<EventSeat sx={{ fontSize: 16 }} />}
                    label={`${library.capacity} Seats`}
                    size="small"
                    sx={{ fontWeight: 500 }}
                  />
                  {(library.open_time || library.close_time) && (
                    <Chip
                      icon={<Schedule sx={{ fontSize: 16 }} />}
                      label={`${library.open_time || '09:00'} - ${library.close_time || '21:00'}`}
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  )}
                </Box>
              </Box>

              {library.amenities && library.amenities.length > 0 && (
                <Box
                  onClick={() => setAmenitiesExpanded((v) => !v)}
                  role="button"
                  tabIndex={0}
                  aria-expanded={amenitiesExpanded}
                  aria-label={amenitiesExpanded ? 'Hide amenities' : 'Show all amenities'}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setAmenitiesExpanded((v) => !v);
                    }
                  }}
                  sx={{
                    flexShrink: 0,
                    maxWidth: '44%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    cursor: 'pointer',
                    borderRadius: 2,
                    px: 1,
                    py: 0.75,
                    bgcolor: 'background.subtle',
                    transition: 'background-color 0.2s ease',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  {library.amenities.slice(0, 3).map((amenity, i) => (
                    <Box key={i} display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
                      <Check color="success" sx={{ fontSize: 15, flexShrink: 0 }} />
                      <Typography variant="caption" fontWeight={500} noWrap sx={{ minWidth: 0 }}>
                        {amenity}
                      </Typography>
                    </Box>
                  ))}
                  <Box display="flex" alignItems="center" gap={0.25} sx={{ color: 'primary.main', mt: 0.25 }}>
                    <Typography variant="caption" fontWeight={700}>
                      {amenitiesExpanded
                        ? 'Show less'
                        : library.amenities.length > 3
                          ? `+${library.amenities.length - 3} more`
                          : 'View all'}
                    </Typography>
                    <ExpandMore
                      sx={{
                        fontSize: 16,
                        transition: 'transform 0.2s ease',
                        transform: amenitiesExpanded ? 'rotate(180deg)' : 'none',
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Box>

            <Collapse in={amenitiesExpanded} timeout="auto" unmountOnExit>
              <Divider sx={{ mb: 1.5 }} />
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                Amenities &amp; Facilities
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {library.amenities && library.amenities.map((amenity, index) => (
                  <Chip
                    key={index}
                    icon={<Check sx={{ fontSize: 16 }} />}
                    label={amenity}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                ))}
              </Box>
            </Collapse>
          </CardContent>
        </MotionCard>

        {/* Plans */}
        <Box mb={3}>
          <Typography variant="h5" fontWeight={700} mb={0}>
            Membership Plans
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            Choose a plan that fits your study schedule
          </Typography>

          {plans.length === 0 ? (
            <Card sx={{ borderRadius: 4, p: 4, textAlign: 'center', bgcolor: 'background.subtle' }}>
              <EventSeat sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" fontWeight={600} gutterBottom>
                No plans available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please check back later
              </Typography>
            </Card>
          ) : (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                overflowX: 'auto',
                pt: 2.5,
                pb: 2,
                '&::-webkit-scrollbar': { height: 8 },
                '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: 4 },
                '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: 4 },
              }}
            >
              {[...plans]
                .sort((a, b) => (a.duration_days || 0) - (b.duration_days || 0))
                .map((plan, index) => (
                <MotionCard
                  key={plan.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileTap={{ scale: 0.98 }}
                  sx={{
                    minWidth: { xs: 280, sm: 320 },
                    maxWidth: { xs: 280, sm: 320 },
                    borderRadius: 4,
                    border: index === 0 ? 2 : 1,
                    borderColor: index === 0 ? 'primary.main' : 'divider',
                    position: 'relative',
                    overflow: 'visible',
                    flexShrink: 0,
                    cursor: 'pointer',
                    bgcolor: index === 0 ? 'primary.soft' : 'background.paper',
                    transition: 'all 0.3s ease',
                    '&:hover': { boxShadow: 8, transform: 'translateY(-8px)' },
                  }}
                >
                  {index === 0 && (
                    <Chip
                      label="MOST POPULAR"
                      size="small"
                      color="primary"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        right: 16,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        boxShadow: 2,
                        animation: `${chipFloat} 3s ease-in-out infinite`,
                        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                      }}
                    />
                  )}

                  {plan.first_time_discount && plan.discounted_price && (
                    <Chip
                      icon={<LocalOffer sx={{ fontSize: 14 }} />}
                      label={isFirstTimeUser ? `${plan.first_time_discount}% OFF` : `${plan.first_time_discount}% off for new users`}
                      size="small"
                      color="warning"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: 16,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        boxShadow: 2,
                        animation: `${chipFloat} 3.4s ease-in-out infinite`,
                        animationDelay: '0.6s',
                        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                      }}
                    />
                  )}

                  <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box mb={2}>
                      <Typography variant="h6" fontWeight={700} gutterBottom>
                        {plan.name}
                      </Typography>
                      {plan.description && (
                        <Typography variant="body2" color="text.secondary" lineHeight={1.5}>
                          {plan.description}
                        </Typography>
                      )}
                    </Box>

                    <Box mb={2}>
                      {plan.first_time_discount && plan.discounted_price ? (
                        <>
                          <Box display="flex" alignItems="baseline" gap={1}>
                            <Typography variant="h3" fontWeight={800} color="primary.main" lineHeight={1}>
                              ₹{plan.discounted_price}
                            </Typography>
                            <Typography variant="h6" fontWeight={500} color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                              ₹{plan.price_cents}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="success.main" fontWeight={600}>
                            {plan.first_time_discount}% off for first-time users
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="h3" fontWeight={800} color="primary.main" lineHeight={1}>
                          ₹{plan.price_cents}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" display="block">
                        for {plan.duration_days} days
                      </Typography>
                    </Box>

                    <Box display="flex" flexDirection="column" gap={1} mb={3}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CalendarToday sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2" fontWeight={500}>
                          {plan.duration_days} days duration
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <AccessTime sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2" fontWeight={500}>
                          {plan.hours_per_day ? `${plan.hours_per_day} hours/day` : 'Unlimited hours'}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <EventSeat sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2" fontWeight={500}>
                          {plan.seats_included} seats included
                        </Typography>
                      </Box>
                      {plan.hours_per_day && (
                        <Chip label={`${plan.hours_per_day}h/day limit`} size="small" color="info" sx={{ alignSelf: 'flex-start', fontWeight: 600 }} />
                      )}
                      {plan.recurring && (
                        <Chip label="Auto-Renewing" size="small" color="secondary" sx={{ alignSelf: 'flex-start', fontWeight: 600 }} />
                      )}
                    </Box>

                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={() => handleBookPlan(plan)}
                      sx={{ borderRadius: 3, py: 1.5, mt: 'auto', fontWeight: 700, fontSize: '0.95rem', boxShadow: 3 }}
                    >
                      Book your Seat
                    </Button>
                  </CardContent>
                </MotionCard>
              ))}
            </Box>
          )}
        </Box>

        {/* Location map */}
        <Divider sx={{ my: 4 }} />
        <Box>
          <Typography variant="h6" fontWeight={700} gutterBottom mb={2}>
            Location
          </Typography>
          {fullAddress && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
              <LocationOn fontSize="small" color="primary" sx={{ mt: '2px', flexShrink: 0 }} />
              <Typography variant="body2" color="text.secondary">
                {fullAddress}
              </Typography>
            </Box>
          )}
          <Chip
            icon={<LocationOn />}
            label="Open in Maps"
            clickable
            component="a"
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        </Box>

        {/* Full-bleed map footer */}
        <LibraryRouteMap library={library} footer />

        {/* Spacer so content clears the sticky WhatsApp bar */}
        <Box
          aria-hidden
          sx={{ height: (!inStudentShell && isAuthenticated) ? { xs: 160, md: 96 } : 96 }}
        />
      </Container>

      {/* Lightbox */}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'rgba(0,0,0,0.95)', borderRadius: 0, m: 0, maxHeight: '100vh' } }}
      >
        <Box sx={{ position: 'relative', width: '100%', height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <IconButton
            onClick={() => setLightboxOpen(false)}
            aria-label="Close lightbox"
            sx={{ position: 'absolute', top: 16, right: 16, color: 'common.white', bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', zIndex: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
          >
            <ArrowBack sx={{ transform: 'rotate(90deg)' }} />
          </IconButton>

          {getGalleryImages().length > 1 && (
            <IconButton onClick={handlePrevImage} aria-label="Previous image" sx={{ position: 'absolute', left: 24, top: '50%', transform: 'translateY(-50%)', color: 'common.white', bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }, zIndex: 2 }}>
              <ArrowBack />
            </IconButton>
          )}

          <Box component="img" src={getGalleryImages()[currentImageIndex]} alt={`${library.name} ${currentImageIndex + 1}`} loading="lazy" decoding="async" sx={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' }} />

          {getGalleryImages().length > 1 && (
            <IconButton onClick={handleNextImage} aria-label="Next image" sx={{ position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%)', color: 'common.white', bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }, zIndex: 2 }}>
              <ArrowBack sx={{ transform: 'rotate(180deg)' }} />
            </IconButton>
          )}

          <Box sx={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', color: 'common.white', bgcolor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(10px)', px: 2, py: 1, borderRadius: 2, fontSize: '0.9rem', fontWeight: 600 }}>
            {currentImageIndex + 1} / {getGalleryImages().length}
          </Box>
        </Box>
      </Dialog>

      {/* Booking dialog — BottomSheet on mobile, Dialog on desktop */}
      {isMobile ? (
        <BottomSheet
          open={bookingDialogOpen}
          onClose={() => setBookingDialogOpen(false)}
          title="Book Your Plan"
          maxHeight="95vh"
        >
          {BookingContent()}
        </BottomSheet>
      ) : (
        <Dialog open={bookingDialogOpen} onClose={() => setBookingDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
          <DialogTitle>
            <Typography variant="h6" fontWeight={700}>
              Book Your Plan
            </Typography>
          </DialogTitle>
          <DialogContent>
            {BookingContent()}
          </DialogContent>
          <DialogActions />
        </Dialog>
      )}

      {/* Booking success dialog */}
      <Dialog open={bookingSuccess} PaperProps={{ sx: { borderRadius: 4, textAlign: 'center', p: 3, maxWidth: 360 } }}>
        <DialogContent>
          <GradientSurface
            variant="brand"
            sx={{ width: 64, height: 64, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', mb: 2 }}
          >
            <CheckCircle sx={{ color: 'common.white', fontSize: 36 }} />
          </GradientSurface>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            {bookingSuccessMessage === 'Booking confirmed'
              ? 'Booking confirmed!'
              : 'Thank you for making the booking at BookMyLibrary!'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {bookingSuccessMessage === 'Booking confirmed'
              ? 'Your seat is confirmed. Redirecting you shortly...'
              : 'Your booking has been submitted for approval. Redirecting you shortly...'}
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Sticky WhatsApp enquiry bar */}
      <Box
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: (!inStudentShell && isAuthenticated) ? { xs: 64, md: 0 } : 0,
          zIndex: (theme) => theme.zIndex.appBar - 1,
          px: 2,
          pt: 1.5,
          pb: 'calc(12px + env(safe-area-inset-bottom))',
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          boxShadow: '0 -4px 16px rgba(15,23,42,0.08)',
        }}
      >
        <Container maxWidth="md" disableGutters>
          <Button
            component="a"
            href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || '15559953705'}?text=${encodeURIComponent(`Hi! I am interested in ${library.name}. Please share more details.`)}`}
            target="_blank"
            rel="noopener noreferrer"
            fullWidth
            variant="contained"
            startIcon={<WhatsApp />}
            sx={{
              py: 1.5,
              borderRadius: 3,
              bgcolor: '#25D366',
              color: 'common.white',
              fontWeight: 600,
              fontSize: '1rem',
              '&:hover': { bgcolor: '#128C7E' },
            }}
          >
            Enquire on WhatsApp
          </Button>
        </Container>
      </Box>

      {!inStudentShell && isAuthenticated && <StudentBottomNav />}

      <Snackbar
        open={shareSnackbar}
        autoHideDuration={2000}
        onClose={() => setShareSnackbar(false)}
        message="Link copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

export default LibraryDetailPage;
